import {
  FiBookOpen,
  FiHome,
  FiSearch,
  FiClipboard,
  FiPackage,
  FiUsers,
  FiTruck,
  FiFileText,
  FiFilePlus,
  FiBarChart2,
  FiSettings,
  FiTool,
  FiGrid,
  FiCreditCard,
  FiShield,
  FiShoppingCart,
  FiTag,
  FiMapPin,
  FiCalendar,
  FiBriefcase,
  FiUserPlus,
  FiClock,
  FiServer,
  FiStar,
  FiUserCheck,
  FiTrendingUp,
  FiInbox,
  FiMessageSquare,
  FiPieChart,
  FiCheckSquare,
  FiBell,
  FiActivity,
  FiList,
  FiLayers,
  FiEdit3,
  FiLock,
  FiDivideCircle,
  FiRepeat,
  FiLink,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import Riyal from '../ui/Riyal';
import TzamunAiIcon from '../ui/TzamunAiIcon';
import { companyHasFeature } from '../../utils/planFeatures';

// Role-based navigation as data. Labels are i18n keys resolved at render time.
//
// `feature` hides the entry when the company's plan does not include that key
// (Layout.js filters on companyHasFeature, the same predicate the server's
// requireFeature uses). Most of these keys ALSO 403 server-side; the ones that
// do not — payment_gateway, and the platform keys — are nav-only on purpose,
// because they name a behaviour inside a core money path where a mid-flow 403
// would strand a job card rather than hide a button.
const MENUS = {
  // Super Admin — grouped by function, same `group` mechanism the Admin menu
  // uses (groupMenuItems below turns it into sections). A flat 22-item list is
  // a wall; these five headings are the questions the operator actually asks:
  // how is the platform doing, who are the tenants, where is the money, what
  // are we telling people, and what runs the thing.
  'Super Admin': [
    { path: '/', icon: FiHome, i18nKey: 'dashboard' },

    { path: '/analytics', icon: FiBarChart2, i18nKey: 'pa.navTitle', group: 'nav.grp.overview' },
    {
      path: '/task-monitoring',
      icon: FiActivity,
      i18nKey: 'taskMonitoring.navTitle',
      group: 'nav.grp.overview',
    },
    { path: '/ha-status', icon: FiServer, i18nKey: 'ha.navTitle', group: 'nav.grp.overview' },

    { path: '/companies', icon: FiGrid, i18nKey: 'companies', group: 'nav.grp.tenants' },
    { path: '/garages', icon: FiMapPin, i18nKey: 'allGarages', group: 'nav.grp.tenants' },
    {
      path: '/subscriptions',
      icon: FiCreditCard,
      i18nKey: 'subscriptions',
      group: 'nav.grp.tenants',
    },
    { path: '/onboarding', icon: FiUserPlus, i18nKey: 'onb.navTitle', group: 'nav.grp.tenants' },
    { path: '/tenant-users', icon: FiUsers, i18nKey: 'tenantUsers', group: 'nav.grp.tenants' },

    {
      path: '/payment-suppliers',
      icon: FiCreditCard,
      i18nKey: 'mfp.paymentSuppliers',
      group: 'nav.grp.financials',
    },
    {
      path: '/managed-payments',
      icon: Riyal,
      i18nKey: 'mp.pageTitle',
      group: 'nav.grp.financials',
    },
    { path: '/payouts', icon: Riyal, i18nKey: 'po.title', group: 'nav.grp.financials' },

    // Everything the platform SAYS — to tenants (announcements), to operators
    // (admin alerts), and the plumbing both ride on. The platform's own
    // WhatsApp handset is NOT here: it lives in Settings › WhatsApp.
    { path: '/announcements', icon: FiBell, i18nKey: 'ann.navTitle', group: 'nav.grp.comms' },
    {
      path: '/admin-notifications',
      icon: FiBell,
      i18nKey: 'adminAlerts.navTitle',
      group: 'nav.grp.comms',
    },
    {
      path: '/notification-channels',
      icon: FiMessageSquare,
      i18nKey: 'notifChannels.navTitle',
      group: 'nav.grp.comms',
    },

    { path: '/users', icon: FiUsers, i18nKey: 'allUsers', group: 'nav.grp.platform' },
    // Path matches the bell-notification link vehicleCatalogMatcher.js stamps
    // (ntf.catalogMiss) — see the matching comment in RoleBasedRouter.js.
    {
      path: '/super-admin/vehicle-catalog',
      icon: FiTruck,
      i18nKey: 'catalog.navTitle',
      group: 'nav.grp.platform',
    },
    { path: '/part-categories', icon: FiTag, i18nKey: 'gc.title', group: 'nav.grp.platform' },
    { path: '/manual-insights', icon: FiBookOpen, i18nKey: 'mi.nav', group: 'nav.grp.platform' },
    {
      path: '/configurations',
      icon: FiSettings,
      i18nKey: 'configurations',
      group: 'nav.grp.platform',
    },
    { path: '/settings', icon: FiSettings, i18nKey: 'settings', group: 'nav.grp.platform' },
  ],
  Staff: [
    { path: '/', icon: FiClipboard, i18nKey: 'myJobs' },
    { path: '/manual', icon: FiBookOpen, i18nKey: 'manual.nav' },
  ],
  // Same headings as the Admin/Manager menu below (owner, 2026-10-09), so a
  // receptionist promoted to manager finds things where they already were.
  Receptionist: [
    { path: '/', icon: FiHome, i18nKey: 'dashboard' },

    { path: '/job-cards', icon: FiClipboard, i18nKey: 'jobCards', group: 'nav.grp.operations' },
    // Same plan gate the backend routes use — an entry that leads to a 403
    // is worse than no entry.
    {
      path: '/appointments',
      icon: FiCalendar,
      i18nKey: 'appointments',
      feature: 'booking_calendar',
      group: 'nav.grp.operations',
    },
    {
      path: '/inspections',
      icon: FiSearch,
      i18nKey: 'inspection.registerTitle',
      feature: 'full_check',
      group: 'nav.grp.operations',
    },

    { path: '/customers', icon: FiUsers, i18nKey: 'customers', group: 'nav.grp.customers' },
    { path: '/vehicles', icon: FiTruck, i18nKey: 'vehicles', group: 'nav.grp.customers' },

    // Only for a Receptionist granted an approve key (not in the baseline role).
    {
      path: '/approvals',
      icon: FiCheckSquare,
      i18nKey: 'appr.inbox.title',
      permissionAny: [
        { resource: 'discounts', action: 'approve' },
        { resource: 'purchase_invoices', action: 'approve' },
      ],
      group: 'nav.grp.followUp',
    },

    {
      path: '/quotations',
      icon: Riyal,
      i18nKey: 'quotations',
      feature: 'quotations',
      group: 'nav.grp.sales',
    },
    {
      path: '/invoices',
      icon: FiFileText,
      i18nKey: 'invoices',
      feature: 'invoicing',
      group: 'nav.grp.sales',
    },

    { path: '/manual', icon: FiBookOpen, i18nKey: 'manual.nav', group: 'nav.grp.help' },
  ],
  // Admin / Manager — grouped by function. `group` drives the section headings
  // the Sidebar renders; ordering here is the rendered order.
  //
  // Order (owner, 2026-10-09): by how often a workshop reaches for it. The
  // floor (Operations), who it is for (Customers & vehicles), what is waiting
  // on you (Follow-up), then money in, money out and stock, the books, the
  // read-only reports, HR and administration. Within a group, daily screens
  // come before setup screens.
  //
  // ORDER IS LOAD-BEARING. groupMenuItems() opens a new section every time the
  // heading CHANGES, so a group whose entries are not contiguous renders its
  // heading once per run. Keep each block together.
  default: [
    { path: '/', icon: FiHome, i18nKey: 'dashboard' },
    {
      path: '/assistant',
      icon: TzamunAiIcon,
      i18nKey: 'assistant.nav',
      feature: 'smart_assistant',
      permission: { resource: 'smart_assistant', action: 'use' },
    },

    { path: '/job-cards', icon: FiClipboard, i18nKey: 'jobCards', group: 'nav.grp.operations' },
    {
      path: '/appointments',
      icon: FiCalendar,
      i18nKey: 'appointments',
      group: 'nav.grp.operations',
      feature: 'booking_calendar',
    },
    {
      path: '/inspections',
      icon: FiSearch,
      i18nKey: 'inspection.registerTitle',
      feature: 'full_check',
      group: 'nav.grp.operations',
    },
    {
      path: '/task-monitoring',
      icon: FiActivity,
      i18nKey: 'taskMonitoring.navTitle',
      group: 'nav.grp.operations',
    },
    // Warranty monitor: which promises are still live, and how long each has
    // left. Operations rather than the reports (owner, 2026-09-07) — the
    // daily reader is the counter clerk taking the call, not the accountant.
    //
    // Deliberately only in `default`. MENUS.Staff is a separate list, so a
    // mechanic never sees this entry — which matters because a garage-wide
    // register contradicts staff scoping (a staff member sees only their own
    // work), and a menu entry with no route in THAT role's tree silently
    // redirects home.
    {
      path: '/warranties',
      icon: FiShield,
      i18nKey: 'wty.navTitle',
      group: 'nav.grp.operations',
      feature: 'service_warranty',
      permission: { resource: 'warranties', action: 'view' },
    },
    { path: '/services', icon: FiTool, i18nKey: 'services', group: 'nav.grp.operations' },

    { path: '/customers', icon: FiUsers, i18nKey: 'customers', group: 'nav.grp.customers' },
    // Shared customers (multi-branch Release 2): the duplicates report and merge.
    {
      path: '/customers/duplicates',
      icon: FiRepeat,
      i18nKey: 'sc.duplicatesTitle',
      group: 'nav.grp.customers',
      feature: 'shared_customers',
      minBranches: 2,
      permission: { resource: 'customers', action: 'merge' },
    },
    { path: '/vehicles', icon: FiTruck, i18nKey: 'vehicles', group: 'nav.grp.customers' },
    {
      path: '/insurance-companies',
      icon: FiBriefcase,
      i18nKey: 'ins.nav',
      group: 'nav.grp.customers',
      permission: { resource: 'insurance_companies', action: 'manage' },
    },
    // Fleet F1 — the plan feature plus fleet.read, the same pair the
    // fleet endpoints stack.
    {
      path: '/fleets',
      icon: FiTruck,
      i18nKey: 'fleet.nav',
      group: 'nav.grp.customers',
      feature: 'fleet_management',
      permission: { resource: 'fleet', action: 'read' },
    },
    // Fleet F5 — the maintenance due list under the fleets entry, same gate.
    {
      path: '/fleets/due',
      icon: FiCalendar,
      i18nKey: 'fleet.due.title',
      group: 'nav.grp.customers',
      feature: 'fleet_management',
      permission: { resource: 'fleet', action: 'read' },
    },

    // Approvals inbox: discounts and purchase invoices above the requester's
    // limit, waiting on this user (GET /api/approvals, same any-of gate).
    // The sidebar badge is its count (Layout).
    {
      path: '/approvals',
      icon: FiCheckSquare,
      i18nKey: 'appr.inbox.title',
      group: 'nav.grp.followUp',
      permissionAny: [
        { resource: 'discounts', action: 'approve' },
        { resource: 'purchase_invoices', action: 'approve' },
      ],
    },
    // Requests inbox: staff task problems, service/part defects, and
    // assistance pings — everything IssueReport surfaces to admins in one
    // list, gated the same as the PATCH resolve endpoint.
    {
      path: '/requests',
      icon: FiInbox,
      i18nKey: 'req.navTitle',
      group: 'nav.grp.followUp',
      permission: { resource: 'issue_reports', action: 'manage' },
    },
    {
      path: '/whatsapp',
      icon: FaWhatsapp,
      i18nKey: 'nav.whatsapp',
      group: 'nav.grp.followUp',
      feature: 'whatsapp',
      permission: { resource: 'whatsapp', action: 'chat' },
    },
    { path: '/feedback', icon: FiStar, i18nKey: 'feedback.title', group: 'nav.grp.followUp' },

    {
      path: '/quotations',
      icon: Riyal,
      i18nKey: 'quotationsEstimates',
      group: 'nav.grp.sales',
      feature: 'quotations',
    },
    {
      path: '/invoices',
      icon: FiFileText,
      i18nKey: 'invoices',
      group: 'nav.grp.sales',
      feature: 'invoicing',
    },
    {
      path: '/payments',
      icon: FiCreditCard,
      i18nKey: 'tp.pageTitle',
      group: 'nav.grp.sales',
      feature: 'payment_gateway',
    },
    {
      path: '/payment-receipts',
      icon: FiFilePlus,
      i18nKey: 'rc.navTitle',
      group: 'nav.grp.sales',
      feature: 'payment_gateway',
      permission: { resource: 'payments', action: 'read' },
    },

    // Unified spending hub — supplier/stock invoices + expenses in one list.
    // Replaces the separate "Purchase Invoices" and "Expenses" menu items; both
    // engines' own pages remain reachable for create/detail deep links.
    {
      path: '/purchases',
      icon: FiShoppingCart,
      i18nKey: 'purchases.nav',
      group: 'nav.grp.purchasesInventory',
      feature: 'inventory',
      permissionAny: [
        { resource: 'expenses', action: 'view' },
        { resource: 'purchase_invoices', action: 'read' },
      ],
    },
    // Supplier directory + per-supplier statement of account.
    {
      path: '/purchases/suppliers',
      icon: FiBriefcase,
      i18nKey: 'sup.title',
      group: 'nav.grp.purchasesInventory',
      feature: 'inventory',
      permission: { resource: 'purchase_invoices', action: 'read' },
    },
    // Shared suppliers (multi-branch Release 2): the duplicates report and merge.
    {
      path: '/purchases/suppliers/duplicates',
      icon: FiRepeat,
      i18nKey: 'ss.duplicatesTitle',
      group: 'nav.grp.purchasesInventory',
      feature: 'shared_suppliers',
      minBranches: 2,
      permission: { resource: 'suppliers', action: 'merge' },
    },
    {
      path: '/parts',
      icon: FiPackage,
      i18nKey: 'parts',
      group: 'nav.grp.purchasesInventory',
      feature: 'inventory',
    },
    {
      path: '/categories',
      icon: FiTag,
      i18nKey: 'partCategories',
      group: 'nav.grp.purchasesInventory',
      feature: 'inventory',
    },
    // Warehouses/stores within this branch. No permission gate on the entry:
    // seeing where a part sits is a read any staff member may need. The
    // create/edit/transfer controls inside are gated on stock:adjust.
    {
      path: '/inventory-locations',
      icon: FiMapPin,
      i18nKey: 'inventoryLocations',
      group: 'nav.grp.purchasesInventory',
      feature: 'inventory',
    },
    // Stock moved between branches (R2): a sender or a receiver sees it.
    {
      path: '/branch-transfers',
      icon: FiRepeat,
      i18nKey: 'bt.nav',
      group: 'nav.grp.purchasesInventory',
      feature: 'branch_stock_transfer',
      permission: { resource: 'stock', action: 'read' },
      permissionAny: [
        { resource: 'stock', action: 'transfer_send' },
        { resource: 'stock', action: 'transfer_receive' },
      ],
    },

    // ── Financials: the books you POST TO ───────────────────────────────────
    //
    // Owner 2026-09-07 split the old sixteen-entry Financials group in two: the
    // books you post to (here) and the statements you read (Reports, below).
    // Every screen here writes something — a movement, a reconciliation, a
    // voucher, a close, an account, an asset. Chart of accounts looks like
    // reference data but creates accounts through AddAccountModal.
    //
    // Owner 2026-10-09: daily work first, setup last (chart of accounts,
    // account mappings, accounting settings), and Purchases moved to
    // Purchases & inventory beside Suppliers and stock.
    {
      path: '/financials/cash',
      icon: Riyal,
      i18nKey: 'cash.nav',
      group: 'nav.grp.financials',
      permission: { resource: 'cash', action: 'view' },
    },
    // Partners (stakeholders) + per-partner statement. Same `cash_movements`
    // flag as Money In/Out — a partner exists only to be drawn from or
    // contributed to, and that key is default-OFF.
    {
      path: '/financials/partners',
      icon: FiUsers,
      i18nKey: 'pt.nav',
      group: 'nav.grp.financials',
      feature: 'cash_movements',
      permission: { resource: 'partners', action: 'view' },
    },
    // Bank reconciliation — statement against books. The default-OFF
    // `bank_reconciliation` key plus bank_recon:view, the same pair the
    // endpoints check. Without this entry the screen has a route and no way in.
    {
      path: '/financials/reconciliation',
      icon: FiCheckSquare,
      i18nKey: 'br.title',
      group: 'nav.grp.financials',
      feature: 'bank_reconciliation',
      permission: { resource: 'bank_recon', action: 'view' },
    },
    // The general ledger's WORKING screens, default-OFF behind `general_ledger`.
    // The statements derived from them are in the reports group below.
    {
      path: '/financials/journal-entries',
      icon: FiBookOpen,
      i18nKey: 'gl.jv.nav',
      group: 'nav.grp.financials',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },
    {
      path: '/financials/period-close',
      icon: FiLock,
      i18nKey: 'gl.close.title',
      group: 'nav.grp.financials',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },
    // Stays visible after sign-off: that is where the audit trail of what was
    // entered lives.
    {
      path: '/financials/opening-balances',
      icon: FiEdit3,
      i18nKey: 'ob.title',
      group: 'nav.grp.financials',
      feature: 'opening_balances',
      permission: { resource: 'opening_balances', action: 'view' },
    },
    // Fixed assets — a register you consult, but you also add and dispose of
    // assets here, so it belongs with the books. The depreciation run itself is
    // automatic; the screen only previews it.
    {
      path: '/financials/fixed-assets',
      icon: FiTool,
      i18nKey: 'fa.title',
      group: 'nav.grp.financials',
      feature: 'general_ledger',
      permission: { resource: 'fixed_assets', action: 'view' },
    },
    {
      path: '/financials/chart-of-accounts',
      icon: FiList,
      i18nKey: 'gl.chart.title',
      group: 'nav.grp.financials',
      feature: 'general_ledger',
      permission: { resource: 'gl_accounts', action: 'view' },
    },
    // Which account each posting role uses (spec 2026-10-05). The mapping
    // routes accept EITHER feature, so the entry shows for either; the read
    // gate is the endpoint's own account_mappings.view.
    {
      path: '/financials/account-mappings',
      icon: FiLink,
      i18nKey: 'gl.acctmap.nav',
      group: 'nav.grp.financials',
      featureAny: ['general_ledger', 'accounting_export'],
      permission: { resource: 'account_mappings', action: 'view' },
    },
    // Accounting policies (fiscal year, warranty provision). Read gate only —
    // the SAVE is gated inside the screen on journal_entries.post, which is the
    // split routes/gl.js makes between GET and PUT /api/gl/settings.
    {
      path: '/financials/accounting-settings',
      icon: FiSettings,
      i18nKey: 'gl.set.nav',
      group: 'nav.grp.financials',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },

    // ── Reports: read-only, in the order an accountant reads them ────────────
    //
    // Owner 2026-10-09: the general Reports screen joins the financial
    // statements, so every read-only report sits under one heading.
    //
    // Every route below sits in the same Admin/Manager block of RoleBasedRouter
    // as the working screens, so a change of HEADING moves no route and no
    // gate. A menu entry whose route is missing from that role's tree silently
    // redirects home, and nothing here left that tree.
    {
      path: '/reports',
      icon: FiBarChart2,
      i18nKey: 'reports',
      group: 'nav.grp.reports',
      feature: 'reports',
    },
    // Where the business stands right now — cash, AR, AP, stock, owner money.
    // NOT a balance sheet (the screen says so); `reports` feature + cash:view,
    // matching the endpoint's own two gates.
    {
      path: '/financials/position',
      icon: FiPieChart,
      i18nKey: 'fp.nav',
      group: 'nav.grp.reports',
      feature: 'reports',
      permission: { resource: 'cash', action: 'view' },
    },
    {
      path: '/financials/trial-balance',
      icon: FiLayers,
      i18nKey: 'gl.tb.title',
      group: 'nav.grp.reports',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },
    // The three IFRS statements, immediately after the trial balance they are
    // derived from. Same pair the endpoints stack —
    // requireFeature('general_ledger') + journal_entries.view — so every role
    // that sees the trial balance sees these three, and no role that does not.
    {
      path: '/financials/income-statement',
      icon: FiTrendingUp,
      i18nKey: 'gl.is.nav',
      group: 'nav.grp.reports',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },
    {
      path: '/financials/cash-flow',
      icon: FiRepeat,
      i18nKey: 'gl.cf.nav',
      group: 'nav.grp.reports',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },
    {
      path: '/financials/changes-in-equity',
      icon: FiDivideCircle,
      i18nKey: 'gl.ce.nav',
      group: 'nav.grp.reports',
      feature: 'general_ledger',
      permission: { resource: 'journal_entries', action: 'view' },
    },
    // Financial audit trail (who posted/edited/paid/reversed, before→after).
    {
      path: '/financials/audit',
      icon: FiClock,
      i18nKey: 'audit.nav',
      group: 'nav.grp.reports',
      permission: { resource: 'financials', action: 'audit_view' },
    },

    // HR module (PR-1): employee records, contracts, documents.
    {
      path: '/hr/employees',
      icon: FiUserCheck,
      i18nKey: 'hr.employees.navTitle',
      group: 'nav.grp.hr',
      permission: { resource: 'hr', action: 'employees.read' },
    },
    // HR payroll + reports (PR-2, Task 11).
    {
      path: '/hr/payroll',
      icon: Riyal,
      i18nKey: 'hr.payroll.navTitle',
      group: 'nav.grp.hr',
      permission: { resource: 'hr', action: 'payroll.read' },
    },
    // The per-employee split of the opening end-of-service provision. Set up
    // once, right after opening balances are signed off, so it sits after the
    // payroll runs it supports rather than among them. Read gate only — the
    // save and the catch-up posting are gated inside the screen on
    // payroll.approve, which is the split routes/hr.js makes across the three
    // endpoints.
    {
      path: '/hr/payroll/eosb-opening-provision',
      icon: FiShield,
      i18nKey: 'hr.eosb.nav',
      group: 'nav.grp.hr',
      permission: { resource: 'hr', action: 'payroll.read' },
    },
    {
      path: '/hr/reports',
      icon: FiBarChart2,
      i18nKey: 'hr.reports.navTitle',
      group: 'nav.grp.hr',
      permission: { resource: 'hr', action: 'reports.read' },
    },
    // HR configuration — its own page since 2026-07-26 (was cards at the
    // bottom of the reports page).
    {
      path: '/hr/settings',
      icon: FiSettings,
      i18nKey: 'hr.settings.navTitle',
      group: 'nav.grp.hr',
      permission: { resource: 'hr', action: 'payroll.manage' },
    },
    // Employee profitability report (PR-5, Task 5) — same gate as the reports above.
    {
      path: '/hr/reports/profitability',
      icon: FiTrendingUp,
      i18nKey: 'hr.profit.title',
      group: 'nav.grp.hr',
      permission: { resource: 'hr', action: 'reports.read' },
    },

    {
      path: '/users',
      icon: FiUsers,
      i18nKey: 'users',
      group: 'nav.grp.admin',
      permission: { resource: 'users', action: 'read' },
    },
    {
      path: '/permissions',
      icon: FiShield,
      i18nKey: 'permissions',
      group: 'nav.grp.admin',
      permission: { resource: 'permissions', action: 'manage' },
    },
    { path: '/settings', icon: FiSettings, i18nKey: 'settings', group: 'nav.grp.admin' },
    { path: '/manual', icon: FiBookOpen, i18nKey: 'manual.nav', group: 'nav.grp.admin' },
  ],
};

export const getMenuItems = (roleName) => MENUS[roleName] || MENUS.default;

/**
 * The feature half of a menu entry's gate: `feature` (one key) or
 * `featureAny` (any of several). Same predicate as the server's requireFeature.
 */
export const menuFeatureAllowed = (company, item) => {
  if (item.feature && !companyHasFeature(company, item.feature)) return false;
  if (
    Array.isArray(item.featureAny) &&
    !item.featureAny.some((f) => companyHasFeature(company, f))
  ) {
    return false;
  }
  // R2 merge screens: nothing new for a one-branch company (spec §3).
  if (item.minBranches && !(Number(company?.branch_count) >= item.minBranches)) return false;
  return true;
};

/**
 * Group an (already permission-filtered) flat menu list into ordered sections
 * for the Sidebar. Walks in order, opening a new section whenever `group`
 * changes; ungrouped items (no `group`) form a leading headingless section.
 * @returns {Array<{ key: string, heading: string|null, items: Array }>}
 */
export const groupMenuItems = (items) => {
  const sections = [];
  let current = null;
  for (const item of items) {
    const heading = item.group || null;
    if (!current || current.heading !== heading) {
      current = { key: heading || `__flat_${sections.length}`, heading, items: [] };
      sections.push(current);
    }
    current.items.push(item);
  }
  return sections;
};
